import { useState, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AutosizeInput from 'react-input-autosize'
import classNames from 'classnames'

import { ReactComponent as ShowDropdownIcon } from '@/assets/icons/editor/show-dropdown.svg'
import { ReactComponent as ChevronUp } from '@/assets/icons/editor/chevron-up.svg'

import writingService from '@/app-domains/messages/draft/writing.service'

import './styles.scoped.sass'

import { manageArrowKeysDropdown } from '@/helpers/arrow-selector-dropdown'

export default function FromSelect (props) {
  const inputRef = useRef(null)
  const {
    defaultDomain,
    draft,
  } = props

  // Live-derived from the boxes store (not a frozen useState snapshot) so a box
  // created moments before composing shows up in the dropdown immediately.
  // Previously this list was captured once at mount, leaving a just-created box
  // unselectable — the user then had to type it, which silently failed to send.
  const storedBoxes = useSelector((state) => state.boxes.boxes)
  const options = useMemo(
    () => Object.values(storedBoxes || {})
      .map((box) => ({ label: box.boxname, value: box.boxname, id: box.composedid }))
      .sort((a, b) => -b.value?.localeCompare(a.value)),
    [storedBoxes]
  )

  const [selectedBox, setSelectedBox] = useState(draft?.from || '')
  const [isOpen, setIsOpen] = useState(false)
  // A dropdown-option click sets this (its mousedown fires before the input's
  // blur) so commit-on-blur doesn't double-fire against the option click.
  const skipBlurRef = useRef(false)

  const openDropdown = (e) => {
    e?.stopPropagation()
    e?.preventDefault()
    if (!isOpen) {
      setIsOpen(true)
      setTimeout(() => document.addEventListener('click', closeDropdown), 50)
      focusInput()
    }
  }

  const closeDropdown = () => {
    setIsOpen(false)
    document.removeEventListener('click', closeDropdown)
  }

  const toggleDropdown = (e) => {
    if (draft?.type === 'reply') return
    isOpen ? closeDropdown(e) : openDropdown(e)
  }

  const updateChosenOption = async (box) => {
    if (!box) return
    await writingService.setFromBox({
      boxName: box.value,
      boxId: box.id
    })
  }

  const updateSelectedBoxValue = (e) => {
    const value = e.target.value.toLowerCase()
    if (value?.length > 0) {
      openDropdown(e)
    }
    setSelectedBox(value)
  }

  const getBoxFromBoxName = (boxName) => {
    const box = options.find((box) => box.label === boxName)
    return box || ({ value: boxName })
  }

  // Commit whatever box name is typed when the field loses focus (e.g. the
  // instant the user clicks Send). Without this, typing a box name and clicking
  // Send with the mouse never wrote `from` to the draft, so the send silently
  // failed with MESSAGE_NOT_READY. Skipped when a dropdown option was just
  // clicked (that path commits on its own). A brand-new box name commits with
  // just the name — the backend creates the box on send.
  const commitTypedBox = () => {
    if (skipBlurRef.current) { skipBlurRef.current = false; return }
    const name = (selectedBox || '').trim().toLowerCase()
    if (!name || draft?.from === name) return
    updateChosenOption(getBoxFromBoxName(name))
  }
  
  const handleOnKeyDown = (e) => {
    manageArrowKeysDropdown(e.keyCode, '.from-select-dropdown-options-choices', 'option-selected-with-arrow')
    if (e.keyCode === 13) {
      const selectedOption = document?.querySelector('.option-selected-with-arrow')
      if (selectedOption) selectedOption.click()
      return
    }
    if (e.keyCode === 13 || e.keyCode === 9 || e.keyCode === 32) {
      closeDropdown()
      updateChosenOption(getBoxFromBoxName(selectedBox))
    }
  }

  const focusInput = () => {
    inputRef?.current?.focus()
  }

  if (!props.isMobile) {
    return (
      <div className='from-select'>
        <label className='from-select-label'>What box do you want to use?</label>
        <div className={classNames('from-select-dropdown', { disabled: draft?.type === 'reply' })} onClick={toggleDropdown}>
          <div className='chosen-option'>
            <AutosizeInput
            ref={inputRef}
            className='chosen-option-box'
            value={selectedBox}
            placeholder="mybox"
            onChange={updateSelectedBoxValue}
            onKeyDown={handleOnKeyDown}
            onBlur={commitTypedBox}
            disabled={draft?.type === 'reply'}
          />
            <span className='chosen-option-domain'>@{defaultDomain}</span>
          </div>
          { draft?.type !== 'reply' && <div className='arrow'>
            {!props.hideDropdownIcon && isOpen ? <ChevronUp/> : <ShowDropdownIcon/>}
          </div> }
        </div>
        { isOpen && options.filter(
          option => option?.label?.length > 0 ? option.label.includes(selectedBox) : true
        ).length > 0 && <div className='from-select-dropdown-options'>
          <div className='from-select-dropdown-options-choices'>
            {options
              .filter(option => option?.label?.length > 0 ? option.label.includes(selectedBox) : true)
              .sort((a, b) => selectedBox ? (a.label.length - b.label.length) : undefined)
              .map((option) => <div
                className={classNames('option', { active: option.label === selectedBox })} key={option.id}
                onMouseDown={() => { skipBlurRef.current = true }}
                onClick={() => {
                  closeDropdown()
                  setSelectedBox(option.label?.toLowerCase())
                  updateChosenOption(option)
                }}
              >
                <span className='box'>{option.label?.toLowerCase()}</span>
                <span className='domain'>@{defaultDomain?.toLowerCase()}</span>
              </div>)
          }
          </div>
        </div>}
      </div>
  
    )
  } else {
    return (<div className='from-select'>
      <div className={classNames('from-select-dropdown', { disabled: draft?.type === 'reply' })} onClick={toggleDropdown}>
        <div className='chosen-option'>
          <AutosizeInput
            className='chosen-option-box'
            value={selectedBox}
            placeholder="mybox"
            onChange={updateSelectedBoxValue}
            onKeyDown={handleOnKeyDown}
            onBlur={commitTypedBox}
            disabled={draft?.type === 'reply'}
          />
          <span className='chosen-option-domain'>@{defaultDomain}</span>
        </div>
        { draft?.type !== 'reply' && <div className='arrow'>
          {!props.hideDropdownIcon && isOpen ? <ChevronUp/> : <ShowDropdownIcon/>}
        </div> }
      </div>
      { isOpen && options.filter(
        option => option?.label?.length > 0 ? option.label.includes(selectedBox) : true
      ).length > 0 && <div className='from-select-dropdown-options'>
        <div className='from-select-dropdown-options-choices'>
          {options
            .filter(option => option?.label?.length > 0 ? option.label.includes(selectedBox) : true)
            .map((option) => <div
          className='option' key={option.id}
          onMouseDown={() => { skipBlurRef.current = true }}
          onClick={() => {
            closeDropdown()
            setSelectedBox(option.label)
            updateChosenOption(option)
          }}
        >
              <span className='box'>{option.label}</span>
              <span className='domain'>@{defaultDomain}</span>
            </div>)
      }
        </div>
      </div>}
    </div>)
  }
}
